import React from 'react';
import './Input.css';

type InputSize = 'sm' | 'md' | 'lg';

/* ── FormField wrapper ─────────────────────────────────────────────────── */

interface FormFieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** htmlFor ties label to the inner control */
  htmlFor?: string;
  children?: React.ReactNode;
  className?: string;
}

function FormField({
  label,
  hint,
  error,
  required = false,
  htmlFor,
  children,
  className = '',
}: FormFieldProps) {
  return (
    <div className={`ui-field ${className}`}>
      {label && (
        <label className="ui-field__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="ui-field__required" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {hint && !error && <span className="ui-field__hint">{hint}</span>}
      {error && <span className="ui-field__error" role="alert">{error}</span>}
    </div>
  );
}

/* ── Input ─────────────────────────────────────────────────────────────── */

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  error?: boolean;
}

function Input({ inputSize = 'md', error = false, className = '', ...rest }: InputProps) {
  const classes = [
    'ui-input',
    inputSize !== 'md' ? `ui-input--${inputSize}` : '',
    error ? 'ui-input--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <input className={classes} {...rest} />;
}

/* ── Textarea ──────────────────────────────────────────────────────────── */

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  inputSize?: InputSize;
  error?: boolean;
}

function Textarea({ inputSize = 'md', error = false, className = '', ...rest }: TextareaProps) {
  const classes = [
    'ui-textarea',
    inputSize !== 'md' ? `ui-textarea--${inputSize}` : '',
    error ? 'ui-textarea--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <textarea className={classes} {...rest} />;
}

/* ── Select ────────────────────────────────────────────────────────────── */

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  inputSize?: InputSize;
  error?: boolean;
}

function Select({ inputSize = 'md', error = false, className = '', ...rest }: SelectProps) {
  const classes = [
    'ui-select',
    inputSize !== 'md' ? `ui-select--${inputSize}` : '',
    error ? 'ui-select--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <select className={classes} {...rest} />;
}

export { FormField, Input, Textarea, Select };
